import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Plus, Pencil, Trash2, Users, Briefcase, Link2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import USPhoneInput from "./USPhoneInput";
import AddressAutocomplete from "./AddressAutocomplete";

const ENTITY_TYPES = ["Individual", "LLC", "Corporation", "Trust", "Partnership"];

/**
 * Format an EIN as `##-#######` (or SSN as `###-##-####` for individuals).
 * Strips non-digits, caps at 9, and inserts the appropriate dashes while typing.
 */
function formatTaxId(raw, entityType) {
  const digits = (raw || "").replace(/\D/g, "").slice(0, 9);
  if (!digits) return "";
  if (entityType === "Individual") {
    // SSN: ###-##-####
    if (digits.length <= 3) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
  }
  // EIN: ##-#######
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}-${digits.slice(2)}`;
}

/** Format raw numeric input as USD with commas, no decimals (e.g. "12500000" → "$12,500,000"). */
function formatCurrency(raw) {
  if (raw === "" || raw == null) return "";
  const digits = String(raw).replace(/[^0-9]/g, "");
  if (!digits) return "";
  return `$${Number(digits).toLocaleString("en-US")}`;
}
function parseCurrency(formatted) {
  if (!formatted) return "";
  return String(formatted).replace(/[^0-9]/g, "");
}

function emptyForm() {
  return {
    name: "",
    email: "",
    phone: "",
    entity_type: "LLC",
    tax_id: "",
    mailing_address: "",
    fico: "",
    years_experience: "",
    total_net_worth: "",
    liquid_assets: "",
    notes: "",
  };
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="overline text-slate-500 mb-1">{label}</div>
      {children}
      {hint && <div className="text-[10px] text-slate-400 mt-1">{hint}</div>}
    </label>
  );
}

/**
 * Borrowers library dialog.
 * - Lists all borrowers for the current landlord.
 * - Inline Add / Edit / Delete.
 * - onBorrowerSelected (optional) is called with a borrower row when the user
 *   clicks "Use" on a row — used by the Add-Loan picker to auto-fill fields.
 */
export default function BorrowersDialog({ open, onOpenChange, onBorrowerSelected, pickerMode = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null = none, "new" = create, or borrower object
  const [form, setForm] = useState(emptyForm());

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/borrowers");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load borrowers"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const openNew = () => { setForm(emptyForm()); setEditing("new"); };
  const openEdit = async (b) => {
    // Fetch single to get full tax_id (listing endpoint strips it)
    try {
      const { data } = await api.get(`/borrowers/${b.id}`);
      setForm({
        name: data.name || "",
        email: data.email || "",
        phone: (data.phone || "").replace(/\D/g, ""),
        entity_type: data.entity_type || "LLC",
        tax_id: data.tax_id || "",
        mailing_address: data.mailing_address || "",
        fico: data.fico ?? "",
        years_experience: data.years_experience ?? "",
        total_net_worth: data.total_net_worth ?? "",
        liquid_assets: data.liquid_assets ?? "",
        notes: data.notes || "",
      });
      setEditing(data);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load borrower"));
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Borrower name required"); return; }
    const payload = {
      ...form,
      fico: form.fico === "" ? null : Number(form.fico),
      years_experience: form.years_experience === "" ? null : Number(form.years_experience),
      total_net_worth: form.total_net_worth === "" ? null : Number(form.total_net_worth),
      liquid_assets: form.liquid_assets === "" ? null : Number(form.liquid_assets),
    };
    try {
      if (editing === "new") {
        await api.post("/borrowers", payload);
        toast.success("Borrower added");
      } else {
        await api.put(`/borrowers/${editing.id}`, payload);
        toast.success("Borrower updated");
      }
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err, "Save failed"));
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete "${b.name}"? Any linked loans will keep the borrower name but lose the library link.`)) return;
    try {
      const { data } = await api.delete(`/borrowers/${b.id}`);
      toast.success(
        data.unlinked_loans > 0
          ? `Deleted. ${data.unlinked_loans} loan${data.unlinked_loans === 1 ? "" : "s"} unlinked.`
          : "Deleted"
      );
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Delete failed"));
    }
  };

  const pick = (b) => {
    if (onBorrowerSelected) {
      onBorrowerSelected(b);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="borrowers-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            {pickerMode ? "Pick a borrower" : "Borrowers Library"}
          </DialogTitle>
        </DialogHeader>

        {editing ? (
          <form onSubmit={save} className="space-y-3">
            <div className="overline text-slate-500">{editing === "new" ? "New borrower" : `Editing: ${editing.name}`}</div>

            <div className="grid grid-cols-2 gap-2.5">
              <Field label="Legal name">
                <input required className="input-clean w-full" data-testid="borrower-name-input"
                  value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})}
                  placeholder="e.g. Acme Holdings LLC" />
              </Field>
              <Field label="Entity type">
                <select className="input-clean w-full" data-testid="borrower-entity-type-select"
                  value={form.entity_type} onChange={(e)=>setForm({...form, entity_type:e.target.value})}>
                  {ENTITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-2.5">
              <Field label="Email">
                <input type="email" className="input-clean w-full" data-testid="borrower-email-input"
                  value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} />
              </Field>
              <Field label="Phone (US)">
                <USPhoneInput data-testid="borrower-phone-input"
                  value={form.phone} onChange={(d) => setForm({...form, phone: d})} />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-2.5">
              <Field label={form.entity_type === "Individual" ? "SSN" : "EIN / Tax ID"} hint="Stored securely; only last-4 visible in listings">
                <input className="input-clean w-full" data-testid="borrower-tax-id-input"
                  value={formatTaxId(form.tax_id, form.entity_type)}
                  onChange={(e) => {
                    // Strip dashes so we always store digits-only; re-format on render.
                    const digits = (e.target.value || "").replace(/\D/g, "").slice(0, 9);
                    setForm({ ...form, tax_id: digits });
                  }}
                  placeholder={form.entity_type === "Individual" ? "XXX-XX-XXXX" : "XX-XXXXXXX"}
                  inputMode="numeric"
                  maxLength={form.entity_type === "Individual" ? 11 : 10} />
              </Field>
              <Field label="Mailing address">
                <AddressAutocomplete
                  value={form.mailing_address}
                  onChange={(v) => setForm({ ...form, mailing_address: v })}
                  onVerified={({ formatted_address }) => setForm({ ...form, mailing_address: formatted_address })}
                  placeholder="Start typing — Google will suggest"
                  dataTestId="borrower-address-input"
                />
              </Field>
            </div>

            <div className="border-t border-slate-200 pt-3">
              <div className="overline text-slate-600 mb-2 flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5" /> Underwriting snapshot</div>
              <div className="grid grid-cols-4 gap-2.5">
                <Field label="FICO">
                  <input type="number" min="300" max="850" className="input-clean w-full" data-testid="borrower-fico-input"
                    value={form.fico} onChange={(e)=>setForm({...form, fico:e.target.value})} />
                </Field>
                <Field label="Years experience">
                  <input type="number" min="0" className="input-clean w-full" data-testid="borrower-years-experience-input"
                    value={form.years_experience} onChange={(e)=>setForm({...form, years_experience:e.target.value})} />
                </Field>
                <Field label="Total net worth">
                  <input type="text" inputMode="numeric" className="input-clean w-full font-mono" data-testid="borrower-net-worth-input"
                    value={formatCurrency(form.total_net_worth)}
                    onChange={(e) => setForm({ ...form, total_net_worth: parseCurrency(e.target.value) })}
                    placeholder="$0" />
                </Field>
                <Field label="Liquid assets">
                  <input type="text" inputMode="numeric" className="input-clean w-full font-mono" data-testid="borrower-liquid-assets-input"
                    value={formatCurrency(form.liquid_assets)}
                    onChange={(e) => setForm({ ...form, liquid_assets: parseCurrency(e.target.value) })}
                    placeholder="$0" />
                </Field>
              </div>
            </div>

            <Field label="Notes">
              <textarea rows={2} className="input-clean w-full" data-testid="borrower-notes-input"
                value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})} />
            </Field>

            <DialogFooter>
              <button type="button" onClick={() => setEditing(null)} className="btn-outline-dark text-sm" data-testid="borrower-cancel-btn">Cancel</button>
              <button type="submit" className="btn-primary-dark text-sm" data-testid="borrower-save-btn">
                {editing === "new" ? "Add borrower" : "Save changes"}
              </button>
            </DialogFooter>
          </form>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <div className="text-xs text-slate-500">
                {loading ? "Loading…" : `${items.length} borrower${items.length === 1 ? "" : "s"} · reusable across all loans`}
              </div>
              <button onClick={openNew} className="btn-primary-dark text-xs flex items-center gap-1.5" data-testid="add-borrower-btn">
                <Plus className="w-3.5 h-3.5" /> Add borrower
              </button>
            </div>

            {items.length === 0 && !loading ? (
              <div className="border border-dashed border-slate-200 rounded-sm p-10 text-center">
                <div className="w-10 h-10 mx-auto bg-slate-100 text-slate-400 rounded-sm flex items-center justify-center mb-3">
                  <Users className="w-5 h-5" />
                </div>
                <div className="text-sm font-semibold text-slate-700 mb-1">No borrowers yet</div>
                <p className="text-xs text-slate-500 max-w-xs mx-auto">Pre-stage sponsors, GPs, and signing entities here. Link them to loans later — edits propagate automatically.</p>
              </div>
            ) : (
              <div className="border border-slate-200 rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left">
                      <th className="overline text-slate-500 px-3 py-2">Name</th>
                      <th className="overline text-slate-500 px-3 py-2">Type</th>
                      <th className="overline text-slate-500 px-3 py-2">Contact</th>
                      <th className="overline text-slate-500 px-3 py-2 text-right">FICO</th>
                      <th className="overline text-slate-500 px-3 py-2 text-right">Net Worth</th>
                      <th className="overline text-slate-500 px-3 py-2 text-right">Loans</th>
                      <th className="overline text-slate-500 px-3 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((b) => (
                      <tr key={b.id} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50" data-testid={`borrower-row-${b.id}`}>
                        <td className="px-3 py-2">
                          <div className="font-semibold text-slate-900">{b.name}</div>
                          {b.tax_id_masked && <div className="text-[10px] text-slate-400 font-mono">{b.tax_id_masked}</div>}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-600">{b.entity_type || "—"}</td>
                        <td className="px-3 py-2 text-xs text-slate-600">
                          {b.email && <div className="truncate max-w-[200px]">{b.email}</div>}
                          {b.phone && <div className="font-mono text-slate-500">{b.phone}</div>}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{b.fico ?? "—"}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">
                          {b.total_net_worth != null ? `$${(b.total_net_worth / 1_000_000).toFixed(1)}M` : "—"}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <span className="inline-flex items-center gap-1 text-[11px] bg-slate-100 text-slate-700 font-mono px-1.5 py-0.5 rounded-sm">
                            <Link2 className="w-3 h-3" />{b.loan_count || 0}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          {pickerMode && (
                            <button onClick={() => pick(b)} className="btn-primary-dark text-[10px] px-2 py-1 mr-1" data-testid={`pick-borrower-${b.id}`}>
                              Use
                            </button>
                          )}
                          <button onClick={() => openEdit(b)} className="text-slate-500 hover:text-slate-900 p-1" data-testid={`edit-borrower-${b.id}`}>
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button onClick={() => remove(b)} className="text-slate-500 hover:text-red-600 p-1" data-testid={`delete-borrower-${b.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
